import axios from 'axios';

/**
 * 지식 베이스 관리를 위한 API 서비스
 */
const kbService = {
  /**
   * 지식 베이스 문서 목록 조회
   * @returns {Promise<Array>} 문서 목록
   */
  async getDocuments() {
    try {
      const response = await axios.get('/api/kb/documents');
      if (response.data.요청결과 === '성공') {
        return response.data.documents;
      }
      throw new Error(response.data.error || '문서 목록 조회 실패');
    } catch (error) {
      console.error('문서 목록 조회 오류:', error);
      throw error;
    }
  },

  /**
   * 지식 베이스 문서 내용 조회
   * @param {string} id - 조회할 문서 ID
   * @returns {Promise<Object>} 문서 내용
   */
  async getDocument(id) {
    try {
      const response = await axios.get(`/api/kb/documents/${id}`);
      if (response.data.요청결과 === '성공') {
        return {
          id: response.data.document._id,
          filename: response.data.document.filename,
          title: response.data.document.title,
          content: response.data.document.content,
          category: response.data.document.category,
          tags: response.data.document.tags,
          createdAt: response.data.document.createdAt,
          updatedAt: response.data.document.updatedAt
        };
      }
      throw new Error(response.data.error || '문서 내용 조회 실패');
    } catch (error) {
      console.error('문서 내용 조회 오류:', error);
      throw error;
    }
  },

  /**
   * 지식 베이스 문서 생성
   * @param {string} title - 문서 제목
   * @param {string} content - 문서 내용
   * @param {string} filename - 파일명 (선택적)
   * @param {string} category - 카테고리 (선택적)
   * @param {Array} tags - 태그 목록 (선택적)
   * @returns {Promise<Object>} 생성 결과
   */
  async createDocument(title, content, filename = '', category = 'general', tags = []) {
    try {
      const response = await axios.post('/api/kb/documents', {
        title,
        content,
        filename: filename || title,
        category,
        tags
      });
      if (response.data.요청결과 === '성공') {
        return {
          documentId: response.data.documentId,
          chunksProcessed: response.data.chunksProcessed,
          totalChunks: response.data.totalChunks
        };
      }
      throw new Error(response.data.error || '문서 생성 실패');
    } catch (error) {
      console.error('문서 생성 오류:', error);
      throw error;
    }
  },

  /**
   * 지식 베이스 문서 수정
   * @param {string} id - 수정할 문서 ID
   * @param {string} title - 문서 제목
   * @param {string} content - 수정할 문서 내용
   * @param {string} filename - 파일명 (선택적)
   * @param {string} category - 카테고리 (선택적)
   * @param {Array} tags - 태그 목록 (선택적)
   * @returns {Promise<Object>} 수정 결과
   */
  async updateDocument(id, title, content, filename = '', category = 'general', tags = []) {
    try {
      const response = await axios.put(`/api/kb/documents/${id}`, {
        title,
        content,
        filename: filename || title,
        category,
        tags
      });
      if (response.data.요청결과 === '성공') {
        return {
          chunksProcessed: response.data.chunksProcessed,
          totalChunks: response.data.totalChunks
        };
      }
      throw new Error(response.data.error || '문서 수정 실패');
    } catch (error) {
      console.error('문서 수정 오류:', error);
      throw error;
    }
  },

  /**
   * 지식 베이스 문서 삭제
   * @param {string} id - 삭제할 문서 ID
   * @returns {Promise<Object>} 삭제 결과
   */
  async deleteDocument(id) {
    try {
      const response = await axios.delete(`/api/kb/documents/${id}`);
      if (response.data.요청결과 === '성공') {
        return {
          documentId: response.data.documentId
        };
      }
      throw new Error(response.data.error || '문서 삭제 실패');
    } catch (error) {
      console.error('문서 삭제 오류:', error);
      throw error;
    }
  },

  /**
   * RAG 기반 챗봇 API 호출
   * @param {string} query - 사용자 질문
   * @param {string} conversationId - 대화 ID (선택적)
   * @returns {Promise<Object>} 챗봇 응답
   */
  async chatWithRAG(query, conversationId = null) {
    try {
      console.log('[KB-SERVICE] RAG API 호출 시작');
      console.log('[KB-SERVICE] 질문:', query);
      console.log('[KB-SERVICE] 대화 ID:', conversationId);

      // 키는 서버가 세션 회사 Settings 에서 꺼낸다 — body 로 안 보냄(cpl-detail-geminiserver-1)
      const response = await axios.post('/api/chat/rag', {
        query,
        conversationId
      });

      console.log('[KB-SERVICE] 서버 응답 받음');
      console.log('[KB-SERVICE] 응답 상태:', response.status);
      console.log('[KB-SERVICE] 응답 데이터:', response.data);

      if (response.data.요청결과 === '성공') {
        const result = {
          response: response.data.response || response.data.응답, // 영문/한글 필드명 모두 지원
          context: response.data.context,
          sources: response.data.sources || response.data.출처, // 영문/한글 필드명 모두 지원
          isRAG: response.data.isRAG || (response.data.sources && response.data.sources.length > 0) || (response.data.출처 && response.data.출처.length > 0)
        };
        console.log('[KB-SERVICE] 파싱된 결과:', result);
        return result;
      }
      throw new Error(response.data.error || '챗봇 응답 실패');
    } catch (error) {
      console.error('[KB-SERVICE] 챗봇 응답 오류:', error);
      if (error.response) {
        console.error('[KB-SERVICE] 응답 상태:', error.response.status);
        console.error('[KB-SERVICE] 응답 데이터:', error.response.data);
      }
      throw error;
    }
  }
};

export default kbService;
